import common from '../common';
import Scene from './Scene';
import CreditsPanel from '../ui/CreditsPanel';
import screenfull from 'screenfull';
import Text from '../ui/Text';
import SplashGlobe from '../SplashGlobe';
import Logger from '../utils/Logger';

export default class SplashScene extends Scene {
	constructor() {
		super();

		this._background = null;
		this._foreground = null;

		this._versionText = null;
		this._earthSprite = null;
		this._earth = null;
		this._undrrLogo = null;
		this._dialog = null;
		this._settingsButton = null;
		this._languageChangeSignal = null;
		this._keyUpSignal = null;

		this._interactionBlocker = null;
		this._earthRenderFrame = 0;

		this._types = [
			'gui.general.disasters.tsunami',
			'gui.general.disasters.hurricane',
			'gui.general.disasters.wildfire',
			'gui.general.disasters.earthquake',
			'gui.general.disasters.flood',
		];

		this._icons = [
			'tsunami',
			'hurricane',
			'wildfire',
			'earthquake',
			'flood',
		];

		this._disasterImagePositions = [
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 500, common.STAGE_HEIGHT * 0.5 - 210),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 500, common.STAGE_HEIGHT * 0.5),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 700, common.STAGE_HEIGHT * 0.5 + 90),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 900, common.STAGE_HEIGHT * 0.5 - 500),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 1300, common.STAGE_HEIGHT * 0.5 + 100),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 300, common.STAGE_HEIGHT * 0.5 + 550),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 1000, common.STAGE_HEIGHT * 0.5 - 400),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 1300, common.STAGE_HEIGHT * 0.5 + 200),
		];

		this._disasterImageScales = [
			0.9, 1.1, 1.5, 1.0, 1.0, 1.0, 1.0, 1.0,
		];

		this._disasterSprites = [];
	}

	init() {
		super.init();

		common.tracking.track(new P3.TrackingDataGtagEvent('game_load', {
			event_category: 'UI',
			event_type: 'game_load',
			page_container: 'game',
		}));

		this.interactive = true;
		this.on('pointertap', () => {
			if (PIXI.utils.isMobile.any && screenfull.isEnabled) {
				screenfull.request();
			}
		});
		this._keyUpSignal = P3.input.signals.keyUp.add(this._onKeyUp, this);

		this._background = new PIXI.Sprite(P3.assets.texture('splash_bg'));

		this._background.anchor.set(0.5);
		this._background.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._background.width = 1800 * P3.View.zoom * 1.25;
		this._background.height = 768 * P3.View.zoom * 1.25;
		this.addChild(this._background);

		this._earth = new SplashGlobe();
		common.tCamera.position.set(0, 0, 2);
		common.tScene.add(this._earth);

		this._earthSprite = new PIXI.Sprite(PIXI.Texture.from($('#three-canvas')[0]));
		this._earthSprite.anchor.set(0.5);
		this._earthSprite.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - 325);
		this.addChild(this._earthSprite);

		this.overlay = new PIXI.Sprite(P3.assets.texture('overlay_full'));
		this.overlay.anchor.set(0.5);
		this.overlay.position.set(this._earthSprite.x - 3, this._earthSprite.y);
		this.overlay.width = this._earthSprite.width;
		this.overlay.scale.y = this.overlay.scale.x;
		this.addChild(this.overlay);
		this.overlay.texture.update();

		this._createDisasterSprites();

		for (let i = 0; i < this._disasterSprites.length; i++) {
			this._countdown(i);
		}

		this._foreground = this._createforeground();
		this.addChild(this._foreground);

		this._interactionBlocker = new PIXI.Graphics();
		this._interactionBlocker.beginFill(0x0, 0.5);
		this._interactionBlocker.drawRect(0, 0, common.STAGE_WIDTH, common.STAGE_HEIGHT);
		this._interactionBlocker.endFill();
		this._interactionBlocker.interactive = true;
		this._interactionBlocker.visible = false;
		this.addChild(this._interactionBlocker);

		this._undrrLogo = new PIXI.Sprite(P3.assets.texture('unisdr'));
		this._undrrLogo.interactive = true;
		this._undrrLogo.buttonMode = true;
		this._undrrLogo.on('pointerup', this._onUNDRRClick);
		this._undrrLogo.anchor.set(0, 1);
		this.addChild(this._undrrLogo);

		this._versionText = new Text(common.config.version, { font: '26px', align: 'left', tint: 0xFFFFFF });
		this._versionText.anchor.set(0, 1);
		this.addChild(this._versionText);

		this._settingsButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_black_fill_small_out'),
			over: P3.assets.texture('btn_black_fill_small_over'),
			icon: P3.assets.texture('icon_settings'),
		});
		this._settingsButton.tabIndex = this._foreground.creditButton.tabIndex + 1;
		this._settingsButton.signals.click.add(this._onSettingsClick, this);
		this.addChild(this._settingsButton);

		this._languageChangeSignal = common.languageManager.signals.changed.add(this.resize, this);
	}

	_countdown(index) {
		new TWEEN.Tween({}, this.tweens)
			.to({}, P3.math.random(10000, 20000))
			.easing(TWEEN.Easing.Linear.None)
			.onComplete(() => {
				this._swapDisasterSprites(index);
			}, this)
			.start();
	}

	_onKeyUp(event) {
		if (P3.scene.top === this && event.key === 'Backspace') {
			if (this._dialog) {
				if (this._dialog.closeButton) {
					this._dialog.closeButton.signals.click.dispatch();
				}

				this._dialog.destroy();
			}

			this._dialog = null;
		}
	}

	_createforeground() {
		const foreground = new PIXI.Container();

		foreground.gametitle = new Text('gui.game.title', {
			dropShadow: true,
			dropShadowAlpha: 0.3,
			dropShadowAngle: 1.5,
			dropShadowBlur: 4,
			dropShadowDistance: 2,
			tint: 0xffffff,
			align: 'center',
			font: '66px',
			fontWeight: 'bold',
			lineJoin: 'round',
			strokeThickness: 2,
			maxWidth: 680,
		});
		foreground.gametitle.anchor.set(0.5, 0);
		foreground.addChild(foreground.gametitle);

		// default drop shadow wasn't enough so we create another text object for extra drop shadow
		foreground.subtitleDropShadow = new Text('gui.game.subtitle', {
			font: '40px',
			fontWeight: 'bold',
			align: 'left',
			tint: 0x0,
			dropShadow: true,
			dropShadowAlpha: 0.75,
			dropShadowAngle: 1.4,
			dropShadowBlur: 6,
			dropShadowDistance: 1,
			padding: 3,
		});
		foreground.subtitleDropShadow.anchor.set(0.5);
		foreground.addChild(foreground.subtitleDropShadow);

		foreground.subtitle = new Text('gui.game.subtitle', {
			font: '40px',
			fontWeight: 'bold',
			align: 'left',
			tint: 0xffffff,
			dropShadow: true,
			dropShadowAlpha: 0.75,
			dropShadowAngle: 1.4,
			dropShadowBlur: 6,
			dropShadowDistance: 1,
			padding: 3,
		});
		foreground.subtitle.anchor.set(0.5);
		foreground.addChild(foreground.subtitle);

		foreground.playerthree = new PIXI.Sprite(P3.assets.texture('logo_playerthree'));
		foreground.playerthree.anchor.set(1, 1);
		foreground.playerthree.scale.set(0.65);
		foreground.addChild(foreground.playerthree);

		foreground.creditText = new Text('gui.splashscreen.develop', { font: '26px', align: 'center', tint: 0xFFFFFF });
		foreground.creditText.anchor.set(1, 1);
		foreground.addChild(foreground.creditText);

		foreground.copyright = new Text(`©2024 | `, { font: '26px', align: 'left', tint: 0xFFFFFF });
		foreground.copyright.anchor.set(1, 1);
		foreground.addChild(foreground.copyright);

		foreground.sourceLink = new Text('getinvolved', { font: '26px', align: 'left', tint: 0xFFFFFF });
		foreground.sourceLink.anchor.set(1, 1);
		foreground.addChild(foreground.sourceLink);

		foreground.sourceLink.interactive = true;
		foreground.sourceLink.buttonMode = true;
		foreground.sourceLink.on('pointerup', this._onSourceClick);

		foreground.sourceLink.underline = new PIXI.Sprite(PIXI.Texture.WHITE);
		foreground.sourceLink.underline.tint = 0xFFFFFF;
		foreground.sourceLink.underline.width = foreground.sourceLink.width;
		foreground.sourceLink.underline.height = 1;
		foreground.sourceLink.underline.anchor.set(1, 1);
		foreground.sourceLink.addChild(foreground.sourceLink.underline);

		const playText = new Text('gui.splashscreen.buttons.play', { font: '34px', align: 'center', fontWeight: 'bold', tint: 0xFFFFFF });
		const installText = new Text('gui.splashscreen.buttons.install', { font: '34px', align: 'center', fontWeight: 'bold', tint: 0xffffff });
		const objectiveText = new Text('gui.splashscreen.buttons.objective', { font: '34px', align: 'center', fontWeight: 'bold', tint: 0xffffff });
		const creditText = new Text('gui.splashscreen.buttons.credits', { font: '34px', align: 'left', fontWeight: 'bold', tint: 0xffffff });

		const longestTextWidth = Math.max(playText.width, installText.width, objectiveText.width, creditText.width);
		const buttonWidth = longestTextWidth + 110;

		/** BUTTONS **/
		foreground.playButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});

		foreground.playButton.signals.click.add(() => {
			this.signals.next.dispatch(this);
		}, this);
		foreground.addChild(foreground.playButton);

		playText.anchor.set(0, 0.5);
		playText.position.set(foreground.playButton.width * -0.5 + 40, 0);
		foreground.playButton.animation.addChild(playText);
		foreground.playButton.accessibleTitle = playText.text;
		foreground.playButton.tabIndex = 1;

		const playIcon = new PIXI.Sprite(P3.assets.texture('icon_play'));

		playIcon.anchor.set(0.5);
		playIcon.position.set(foreground.playButton.width * 0.5 - 40, 0);
		foreground.playButton.animation.addChild(playIcon);

		if (window.deferredPrompt) {
			foreground.installButton = common.buttonFactory.createButton({
				normal: { textureName: 'btn_dark_blue_fill_large_9slice_out', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
				over: { textureName: 'btn_dark_blue_fill_large_9slice_over', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			});

			foreground.installButton.signals.click.add(this._onInstallClick, this);
			foreground.addChild(foreground.installButton);

			installText.anchor.set(0, 0.5);
			installText.position.set(foreground.installButton.width * -0.5 + 40, 0);
			foreground.installButton.animation.addChild(installText);
			foreground.installButton.accessibleTitle = installText.text;
			foreground.installButton.tabIndex = 2;

			const installIcon = new PIXI.Sprite(P3.assets.texture('icon_install'));

			installIcon.anchor.set(0.5);
			installIcon.position.set(foreground.installButton.width * 0.5 - 40, 0);
			foreground.installButton.animation.addChild(installIcon);
		}

		foreground.objectiveButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_dark_blue_fill_large_9slice_out', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_dark_blue_fill_large_9slice_over', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});

		foreground.objectiveButton.signals.click.add(this._onObjectiveClick, this);
		foreground.addChild(foreground.objectiveButton);

		foreground.objectiveButton.label = objectiveText;
		foreground.objectiveButton.label.anchor.set(0.5);
		foreground.objectiveButton.animation.addChild(foreground.objectiveButton.label);

		foreground.objectiveButton.accessibleTitle = foreground.objectiveButton.label.text;
		foreground.objectiveButton.tabIndex = foreground.installButton ? 3 : 2;

		foreground.creditButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_dark_blue_fill_large_9slice_out', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_dark_blue_fill_large_9slice_over', width: buttonWidth, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});

		foreground.creditButton.signals.click.add(this._onCreditClick, this);
		foreground.addChild(foreground.creditButton);

		foreground.creditButton.label = creditText;
		foreground.creditButton.label.anchor.set(0.5);
		foreground.creditButton.animation.addChild(foreground.creditButton.label);

		foreground.creditButton.accessibleTitle = foreground.creditButton.label.text;
		foreground.creditButton.tabIndex = foreground.objectiveButton.tabIndex + 1;

		return foreground;
	}

	_swapDisasterSprites(index) {
		const a = new TWEEN.Tween(this._disasterSprites[index], this.tweens)
			.to({ alpha: 0 }, 500)
			.easing(TWEEN.Easing.Linear.None);

		const b = new TWEEN.Tween(this._disasterSprites[index], this.tweens)
			.to({ alpha: 1 }, 500)
			.easing(TWEEN.Easing.Linear.None)
			.onStart(() => {
				const container = this._disasterSprites[index];
				const disaster = P3.utils.selectRandom(this._icons);

				container.disasterImage.texture = P3.assets.texture(`${disaster}_${P3.utils.padNum(P3.math.randomInt(1, 5), 2)}`);
				container.icon.texture = P3.assets.texture(`icon_${disaster}`);

				this._countdown(index);
			});

		a.chain(b);
		a.start();
	}

	_createDisasterSprites() {
		for (let i = 0; i < this._disasterImagePositions.length; i++) {
			const disaster = P3.utils.selectRandom(this._icons);

			const container = new PIXI.Container();

			container.position.set(this._disasterImagePositions[i].x, this._disasterImagePositions[i].y);
			this.addChild(container);

			const underglow = new PIXI.Sprite(P3.assets.texture('red_glow_25pc'));

			underglow.anchor.set(0.5);
			underglow.scale.set(3.5);
			underglow.position.set(0, 0);
			container.addChild(underglow);

			container.disasterImage = new PIXI.Sprite(P3.assets.texture(`${disaster}_${P3.utils.padNum(P3.math.randomInt(1, 5), 2)}`));
			container.disasterImage.anchor.set(0.5);
			container.addChild(container.disasterImage);

			const redline = new PIXI.Sprite(PIXI.Texture.WHITE);

			redline.tint = 0xff0000;
			redline.width = 1;
			redline.anchor.set(0.5);
			redline.height = container.disasterImage.height;
			redline.position.set(container.disasterImage.width * -0.5, 0);
			container.disasterImage.addChild(redline);

			container.icon = new PIXI.Sprite(P3.assets.texture(`icon_${disaster}`));
			container.icon.anchor.set(0.5);
			container.icon.scale.set(0.5);
			container.icon.position.set(0, 0);
			container.addChild(container.icon);

			container.disasterImage.scale.set(this._disasterImageScales[i]);

			this._disasterSprites.push(container);
		}
	}

	appear() {
		super.appear();
		this.resize();
	}

	resize() {
		super.resize();

		this._disasterImagePositions = [
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 500, common.STAGE_HEIGHT * 0.5 - 210),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 600, common.STAGE_HEIGHT * 0.5),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 700, common.STAGE_HEIGHT * 0.5 + 90),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 900, common.STAGE_HEIGHT * 0.5 - 500),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 1300, common.STAGE_HEIGHT * 0.5 + 100),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 - 400, common.STAGE_HEIGHT * 0.5 + 550),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 1000, common.STAGE_HEIGHT * 0.5 - 400),
			new PIXI.Point(common.STAGE_WIDTH * 0.5 + 1300, common.STAGE_HEIGHT * 0.5 + 200),
		];

		for (let i = 0; i < this._disasterSprites.length; i++) {
			const sprite = this._disasterSprites[i];

			sprite.position.set(this._disasterImagePositions[i].x, this._disasterImagePositions[i].y);
		}

		this._interactionBlocker.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
		this._interactionBlocker.width = P3.View.width;
		this._interactionBlocker.height = P3.View.height;

		this._settingsButton.position.set((common.STAGE_WIDTH + P3.View.width) * 0.5 - 70, 70);
		this._versionText.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 25, common.STAGE_HEIGHT - 20);
		this._undrrLogo.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 5, common.STAGE_HEIGHT - 20);
		this._earthSprite.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.1);

		P3.utils.performNextTick(() => {
			this.overlay.position.set(this._earthSprite.x - 3, this._earthSprite.y);
			this.overlay.width = this._earthSprite.width * 1.22;
			this.overlay.scale.y = this.overlay.scale.x;
		});

		this._background.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._background.width = 1800 * P3.View.zoom * 1.25;
		this._background.height = 768 * P3.View.zoom * 1.25;

		const { gametitle, subtitle, subtitleDropShadow, copyright, playerthree, creditText, playButton, installButton, objectiveButton, creditButton, sourceLink } = this._foreground;

		gametitle.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - 200);
		subtitle.position.set(gametitle.x, gametitle.y - 20);
		subtitleDropShadow.position.set(gametitle.x, gametitle.y - 20);
		playButton.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);

		let nextY = playButton.y + 140;

		if (installButton) {
			installButton.position.set(playButton.x, nextY);
			nextY = installButton.y + 90;
		}

		objectiveButton.position.set(playButton.x, nextY);
		creditButton.position.set(objectiveButton.x, objectiveButton.y + 90);

		sourceLink.position.set((P3.View.width + common.STAGE_WIDTH) * 0.5 - 25, common.STAGE_HEIGHT - 20);
		copyright.position.set(sourceLink.x - sourceLink.width - 10, sourceLink.y);
		playerthree.position.set(copyright.x - copyright.width - 10, copyright.y + 8);
		creditText.position.set(playerthree.x - playerthree.width - 10, copyright.y);

		if (this._dialog) {
			this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		}
	}

	destroy() {
		this._earth.destroy();
		common.languageManager.signals.changed.detach(this._languageChangeSignal, this);
		P3.input.signals.keyUp.detach(this._keyUpSignal);

		super.destroy();
	}

	_onObjectiveClick() {
		window.open('https://www.preventionweb.net/stop-disasters-game/about', '_blank');
	}

	_onUNDRRClick() {
		window.open('https://www.preventionweb.net/stop-disasters-game/about', '_blank');
	}

	_onSourceClick() {
		window.open('https://www.preventionweb.net/stop-disasters-game/get-involved', '_blank');
	}

	_onSettingsClick() {
		this.signals.pause.dispatch();
	}

	_onCreditClick() {
		if (this._dialog) {
			return;
		}

		this._interactionBlocker.visible = true;

		this._dialog = new CreditsPanel(900, 600, { _func: this.onDialogClose, _scope: this });
		this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this.addChild(this._dialog);
	}

	_onInstallClick() {
		if (window.deferredPrompt) {
			window.deferredPrompt.prompt();
			window.deferredPrompt.userChoice.then((choiceResult) => {
				if (choiceResult.outcome === 'accepted') {
					Logger.log('User accepted the PWA prompt');
				} else {
					Logger.log('User dismissed the PWA prompt');
				}
				window.deferredPrompt = null;
			});
		}
	}

	onDialogClose() {
		this._interactionBlocker.visible = false;
		P3.utils.delay(0.1, () => {
			this._dialog = null;
		});
	}

	renderEarth() {
		if (this._earth && !this._earth.globeDisabled) {
			this._earth.update();
			this._earthSprite.texture.update();
		}
	}

	update() {
		super.update();
		this._dialog && this._dialog.update();
		this.renderEarth();
	}
}
